/**
 * Client-side account self-service calls for self-auth users.
 *
 * These hit the backend `/api/auth/*` surface directly with a Bearer token
 * (obtained from `useAuthContext().getToken`), NOT the BFF `/api/session/*`
 * proxy — those are only for cookie-establishing flows. Each function takes
 * the token so it stays a plain, testable module (no React hooks inside).
 */
import {
  actionErrorFromResponse,
  actionErrorFromThrown,
  localActionError,
  type ActionError,
} from '@/lib/action-result'
import { getApiBaseUrl } from '@/lib/api-config'

export interface IdentitySummary {
  provider: string
  email: string | null
}

export interface ProfileData {
  id: string
  email: string
  first_name: string | null
  last_name: string | null
  avatar_url: string | null
  has_password: boolean
  identities: IdentitySummary[]
}

export interface ProfilePatch {
  first_name?: string | null
  last_name?: string | null
  avatar_object_key?: string | null
}

/**
 * Re-exported so this module's callers keep their import; the shape and its
 * "never render `error`" contract live in `@/lib/action-result`.
 */
export type { ActionError } from '@/lib/action-result'

interface ApiEnvelope<T> {
  success?: boolean
  data?: T | null
  error?: { code?: string; message?: string } | null
}

const toError = actionErrorFromResponse

function authHeaders(token: string): HeadersInit {
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }
}

export async function fetchProfile(
  token: string,
): Promise<{ ok: true; profile: ProfileData } | ActionError> {
  let resp: Response
  try {
    // web-arch-allow-direct-fetch: self-auth account calls hit /api/auth/* directly with a Bearer token (not the BFF / use-api hooks); see file header.
    resp = await fetch(`${getApiBaseUrl()}/api/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
      cache: 'no-store',
    })
  } catch (err) {
    return actionErrorFromThrown(err)
  }
  if (!resp.ok) return toError(resp)
  const body = (await resp.json()) as ApiEnvelope<{
    user?: Partial<ProfileData> & { id: string; email: string; name?: string }
  }>
  const user = body.data?.user
  if (!user)
    return localActionError('empty_response', 'response carried no data')
  return {
    ok: true,
    profile: {
      id: user.id,
      email: user.email,
      first_name: user.first_name ?? null,
      last_name: user.last_name ?? null,
      avatar_url: user.avatar_url ?? null,
      has_password: user.has_password ?? false,
      identities: user.identities ?? [],
    },
  }
}

export async function updateProfile(
  token: string,
  patch: ProfilePatch,
): Promise<{ ok: true; profile: ProfileData } | ActionError> {
  let resp: Response
  try {
    // web-arch-allow-direct-fetch: self-auth account calls hit /api/auth/* directly with a Bearer token (not the BFF / use-api hooks).
    resp = await fetch(`${getApiBaseUrl()}/api/auth/me`, {
      method: 'PATCH',
      headers: authHeaders(token),
      body: JSON.stringify(patch),
      cache: 'no-store',
    })
  } catch (err) {
    return actionErrorFromThrown(err)
  }
  if (!resp.ok) return toError(resp)
  const body = (await resp.json()) as ApiEnvelope<Partial<ProfileData>>
  const data = body.data
  if (!data || !data.id || !data.email) {
    return localActionError('empty_response', 'response carried no data')
  }
  return {
    ok: true,
    profile: {
      id: data.id,
      email: data.email,
      first_name: data.first_name ?? null,
      last_name: data.last_name ?? null,
      avatar_url: data.avatar_url ?? null,
      has_password: data.has_password ?? false,
      identities: data.identities ?? [],
    },
  }
}

/**
 * Set an initial password for accounts that have none yet (e.g. Google-only
 * sign-ups). Unlike `changePassword`, this takes no current password — the
 * live session authorizes it. On success the backend revokes every refresh
 * token, so the caller must sign the user out afterwards.
 */
export async function setPassword(
  token: string,
  newPassword: string,
): Promise<{ ok: true } | ActionError> {
  let resp: Response
  try {
    // web-arch-allow-direct-fetch: self-auth account calls hit /api/auth/* directly with a Bearer token (not the BFF / use-api hooks).
    resp = await fetch(`${getApiBaseUrl()}/api/auth/me/password/set`, {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify({ new_password: newPassword }),
      cache: 'no-store',
    })
  } catch (err) {
    return actionErrorFromThrown(err)
  }
  if (!resp.ok) return toError(resp)
  return { ok: true }
}

/**
 * Unlink the Google identity from the current account. The backend refuses
 * (`cannot_unlink_last_method`) when Google is the only remaining sign-in
 * method, so the account can never be locked out.
 */
export async function unlinkGoogle(
  token: string,
): Promise<{ ok: true } | ActionError> {
  let resp: Response
  try {
    // web-arch-allow-direct-fetch: self-auth account calls hit /api/auth/* directly with a Bearer token (not the BFF / use-api hooks).
    resp = await fetch(`${getApiBaseUrl()}/api/auth/me/identities/google`, {
      method: 'DELETE',
      headers: authHeaders(token),
      cache: 'no-store',
    })
  } catch (err) {
    return actionErrorFromThrown(err)
  }
  if (!resp.ok) return toError(resp)
  return { ok: true }
}

export async function changePassword(
  token: string,
  currentPassword: string,
  newPassword: string,
): Promise<{ ok: true } | ActionError> {
  let resp: Response
  try {
    // web-arch-allow-direct-fetch: self-auth account calls hit /api/auth/* directly with a Bearer token (not the BFF / use-api hooks).
    resp = await fetch(`${getApiBaseUrl()}/api/auth/me/password`, {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
      cache: 'no-store',
    })
  } catch (err) {
    return actionErrorFromThrown(err)
  }
  if (!resp.ok) return toError(resp)
  return { ok: true }
}

/**
 * Step 1 of an email change: ask the backend to mail a 6-digit code to
 * `newEmail`. Nothing about the account changes until the code is confirmed.
 *
 * Failure codes worth branching on: `same_email`, `email_taken`,
 * `email_not_allowed`, `signup_domain_not_allowed`. A 429 arrives as a bare
 * rate-limit HTTPException, so it carries `status` but no domain `code`.
 */
export async function requestEmailChange(
  token: string,
  newEmail: string,
): Promise<{ ok: true } | ActionError> {
  let resp: Response
  try {
    // web-arch-allow-direct-fetch: self-auth account calls hit /api/auth/* directly with a Bearer token (not the BFF / use-api hooks).
    resp = await fetch(`${getApiBaseUrl()}/api/auth/me/email/change-request`, {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify({ new_email: newEmail }),
      cache: 'no-store',
    })
  } catch (err) {
    return actionErrorFromThrown(err)
  }
  if (!resp.ok) return toError(resp)
  return { ok: true }
}

/**
 * Step 2: redeem the code. On success the backend has already revoked every
 * session, so the caller must sign out and sign back in with the returned
 * address — the current token is dead.
 *
 * Failure codes: `invalid_credentials` (wrong code), `email_change_expired`
 * (no live request), `email_taken` (claimed in the meantime).
 */
export async function confirmEmailChange(
  token: string,
  code: string,
): Promise<{ ok: true; email: string } | ActionError> {
  let resp: Response
  try {
    // web-arch-allow-direct-fetch: self-auth account calls hit /api/auth/* directly with a Bearer token (not the BFF / use-api hooks).
    resp = await fetch(`${getApiBaseUrl()}/api/auth/me/email/change-confirm`, {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify({ code }),
      cache: 'no-store',
    })
  } catch (err) {
    return actionErrorFromThrown(err)
  }
  if (!resp.ok) return toError(resp)
  const body = (await resp.json()) as ApiEnvelope<{ email?: string }>
  const email = body.data?.email
  if (!email)
    return localActionError('empty_response', 'response carried no data')
  return { ok: true, email }
}

/**
 * Full avatar flow: request a presigned PUT, upload the file to it, and
 * return the object key for the caller to persist via `updateProfile`.
 */
export async function uploadAvatar(
  token: string,
  file: File,
): Promise<{ ok: true; objectKey: string } | ActionError> {
  let resp: Response
  try {
    // web-arch-allow-direct-fetch: self-auth account calls hit /api/auth/* directly with a Bearer token (not the BFF / use-api hooks).
    resp = await fetch(`${getApiBaseUrl()}/api/auth/me/avatar/upload-url`, {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify({ filename: file.name, content_type: file.type }),
      cache: 'no-store',
    })
  } catch (err) {
    return actionErrorFromThrown(err)
  }
  if (!resp.ok) return toError(resp)
  const body = (await resp.json()) as ApiEnvelope<{
    object_key: string
    upload_url: string
  }>
  if (!body.data)
    return localActionError('empty_response', 'response carried no data')

  // The presigned PUT is signed for this exact Content-Type — send it back.
  let putResp: Response
  try {
    putResp = await fetch(body.data.upload_url, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    })
  } catch (err) {
    return actionErrorFromThrown(err)
  }
  if (!putResp.ok)
    return {
      ok: false,
      error: `avatar upload PUT failed (${putResp.status})`,
      code: 'upload_failed',
      status: putResp.status,
    }
  return { ok: true, objectKey: body.data.object_key }
}
